// A date (and date-time) field of the admin (DESIGN.md «Компоненты»): typed as «дд.мм.гггг», picked on a month grid in a popover.
// Replaces the browser's own date input, whose look, language and calendar differ between browsers and themes.

import { forwardRef, useEffect, useState, type InputHTMLAttributes } from 'react';
import * as PO from '@radix-ui/react-popover';
import { Icon } from './icons.tsx';
import { cx } from './primitives.tsx';
import s from './date.module.css';

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

/** «2026-10-28» or «2026-10-28T09:30» → «28.10.2026» / «28.10.2026 09:30»; anything else → as is. */
export function showDate(v: string, time = false): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(v);
  if (!m) return v;
  return `${m[3]}.${m[2]}.${m[1]}${time ? ` ${m[4] ?? '00'}:${m[5] ?? '00'}` : ''}`;
}

/** «28.10.2026», «28.10.26», «2026-10-28», with « 09:30» when `time` → the value; '' for empty; null when it is not a date. */
export function parseDate(text: string, time = false): string | null {
  const t = text.trim();
  if (!t) return '';
  const m = /^(?:(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})|(\d{4})-(\d{2})-(\d{2}))(?:[ T](\d{1,2}):(\d{2}))?$/.exec(t);
  if (!m) return null;
  const y = m[4] ? +m[4] : m[3]!.length === 2 ? 2000 + +m[3]! : +m[3]!;
  const mo = (m[5] ? +m[5] : +m[2]!) - 1, d = m[6] ? +m[6] : +m[1]!;
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  if (!time) return iso(y, mo, d);
  const h = m[7] ? +m[7] : 0, mi = m[8] ? +m[8] : 0;
  return h < 24 && mi < 60 ? `${iso(y, mo, d)}T${pad(h)}:${pad(mi)}` : null;
}

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  /** «YYYY-MM-DD», with `time` «YYYY-MM-DDTHH:mm»; '' — empty. */
  value: string;
  onChange: (value: string) => void;
  time?: boolean;
  bare?: boolean;
};

export const DateInput = forwardRef<HTMLInputElement, Props>(function DateInput({ value, onChange, time, bare, className, onBlur, disabled, ...rest }, ref) {
  const [text, setText] = useState(() => showDate(value, time));
  const [open, setOpen] = useState(false);
  const [bad, setBad] = useState(false);
  // A value changed from outside (another person, the calendar) shows as typed text, unless the person is typing that very value.
  useEffect(() => { if (parseDate(text, time) !== value) { setText(showDate(value, time)); setBad(false); } }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const cur = /^(\d{4})-(\d{2})/.exec(value);
  const today = new Date();
  const [month, setMonth] = useState<[number, number]>(cur ? [+cur[1]!, +cur[2]! - 1] : [today.getFullYear(), today.getMonth()]);
  useEffect(() => { if (open && cur) setMonth([+cur[1]!, +cur[2]! - 1]); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const typed = (t: string) => {
    setText(t);
    const v = parseDate(t, time);
    setBad(v === null);
    if (v !== null && v !== value) onChange(v);
  };
  const pick = (y: number, m: number, d: number) => {
    const v = time ? `${iso(y, m, d)}T${value.slice(11, 16) || '00:00'}` : iso(y, m, d);
    onChange(v); setText(showDate(v, time)); setBad(false); setOpen(false);
  };
  const [y, m] = month;
  const first = (new Date(y, m, 1).getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)] as (number | null)[];
  const sel = value.slice(0, 10), now = iso(today.getFullYear(), today.getMonth(), today.getDate());
  const shift = (n: number) => setMonth(([yy, mm]) => { const d = new Date(yy, mm + n, 1); return [d.getFullYear(), d.getMonth()]; });

  return (
    <PO.Root open={open} onOpenChange={setOpen}>
      <PO.Anchor asChild>
        <span className={cx(s.wrap, className)}>
          <input ref={ref} {...rest} disabled={disabled} className={cx(s.input, bare && s.bare)} inputMode="numeric" value={text}
            placeholder={time ? 'дд.мм.гггг чч:мм' : 'дд.мм.гггг'} aria-invalid={bad || rest['aria-invalid'] === true || undefined}
            onChange={(e) => typed(e.target.value)}
            onBlur={(e) => { if (!bad) setText(showDate(value, time)); onBlur?.(e); }} />
          <PO.Trigger asChild>
            <button type="button" className={s.btn} disabled={disabled} aria-label="Выбрать дату" tabIndex={-1}
              // The calendar keeps the focus in the field: a table cell does not take the click as leaving the editor.
              onMouseDown={(e) => e.preventDefault()}><Icon name="date" size={14} /></button>
          </PO.Trigger>
        </span>
      </PO.Anchor>
      <PO.Portal>
        <PO.Content className={s.pop} align="start" sideOffset={4} collisionPadding={8} onOpenAutoFocus={(e) => e.preventDefault()} onMouseDown={(e) => e.preventDefault()}>
          <div className={s.head}>
            <button type="button" className={s.nav} aria-label="Предыдущий месяц" onClick={() => shift(-1)}><Icon name="chevronLeft" size={14} /></button>
            <span className={s.title}>{MONTHS[m]} {y}</span>
            <button type="button" className={s.nav} aria-label="Следующий месяц" onClick={() => shift(1)}><Icon name="chevronRight" size={14} /></button>
          </div>
          <div className={s.grid} role="grid" aria-label={`${MONTHS[m]} ${y}`}>
            {DAYS.map((d) => <span key={d} className={s.dow}>{d}</span>)}
            {cells.map((d, i) => d === null ? <span key={`e${i}`} /> : (
              <button key={d} type="button" className={cx(s.day, iso(y, m, d) === sel && s.on, iso(y, m, d) === now && s.today)} aria-pressed={iso(y, m, d) === sel} onClick={() => pick(y, m, d)}>{d}</button>
            ))}
          </div>
          <div className={s.foot}>
            <button type="button" className={s.link} onClick={() => pick(today.getFullYear(), today.getMonth(), today.getDate())}>Сегодня</button>
            {value && <button type="button" className={s.link} onClick={() => { onChange(''); setText(''); setOpen(false); }}>Очистить</button>}
          </div>
        </PO.Content>
      </PO.Portal>
    </PO.Root>
  );
});
